// Svelte-free by design, so a `node:test` file can exercise it.

/**
 * Lo stato di lettura delle fatture ricevute, lato interfaccia.
 *
 * Una fattura ricevuta è «da leggere» finché chi lavora le fatture non la apre
 * o non la segna come letta; le emesse non hanno uno stato di lettura. Filtro,
 * conteggio e marcature li applica il server: qui si decide come mostrarli.
 */

/** Valori del filtro «Lettura» della scheda Ricevute; '' = tutte. */
export const LETTA_FILTER_VALUES = Object.freeze(['', 'false']);

/** L'etichetta di ogni valore del filtro. */
export const LETTA_FILTER_LABELS = Object.freeze({
  '': 'invoices.readState.filter.all',
  false: 'invoices.readState.filter.unread',
});

/** `?letta=` → un valore del filtro; quello che non conosce diventa «Tutte». */
export function seedLettaFilter(raw) {
  return raw === 'false' ? 'false' : '';
}

/** Una fattura ricevuta che nessuno ha ancora letto. */
export function isUnread(row) {
  return row?.direction === 'inbound' && row?.isRead === false;
}

/** Gli id, tra le righe selezionate, a cui si applica lo stato di lettura: le ricevute. */
export function readStateIds(rows) {
  return rows.filter((row) => row?.direction === 'inbound').map((row) => row.id);
}

/** Il numero sul contatore: oltre 99 diventa «99+». */
export function badgeText(count) {
  const n = Number(count) || 0;
  return n > 99 ? '99+' : String(n);
}

/**
 * Si può rimettere tra quelle da leggere: una fattura ricevuta che non sia la
 * copia di un'autofattura. Quella è un documento dell'azienda stessa, e il
 * server la tiene sempre letta (`pagamento.autofattura`).
 */
export function canMarkUnread(invoice) {
  return invoice?.direction === 'inbound' && invoice?.pagamento?.autofattura !== true;
}

/**
 * Il contatore delle ricevute da leggere, senza Svelte: chi lo usa passa come
 * leggere il numero, come sapere quale azienda è attiva e dove scriverlo.
 *
 * Un numero vale solo per l'azienda attiva. Una richiesta partita per l'azienda
 * A e arrivata dopo il passaggio a B non deve scrivere sul contatore di B; e una
 * pagina di A che chiede di aggiornare quando ormai è attiva B non chiede
 * niente. Tra due richieste per la stessa azienda vince la più recente.
 *
 * `apply` receives the company the count belongs to: the store keeps the pair,
 * and shownUnread() shows it only while that company is active, so a switch
 * from A to B hides A's figure at once instead of when B answers.
 *
 * @param {{fetchCount: (companyId: string) => Promise<{inbound: number}>,
 *   isActive: (companyId: string) => boolean, apply: (count: number, companyId: string|null) => void}} deps
 * @returns {(companyId: string|null|undefined) => Promise<void>}
 */
export function createUnreadRefresher({ fetchCount, isActive, apply }) {
  let latest = 0;
  return async function refresh(companyId) {
    if (!companyId) {
      latest += 1;
      apply(0, null);
      return;
    }
    if (!isActive(companyId)) return;
    const ticket = ++latest;
    let count = 0;
    try {
      count = Number((await fetchCount(companyId))?.inbound) || 0;
    } catch {
      count = 0;
    }
    if (ticket === latest && isActive(companyId)) apply(count, companyId);
  };
}

/**
 * Il numero da mostrare: quello letto per l'azienda attiva, altrimenti zero —
 * nessun contatore — finché la nuova azienda non risponde.
 *
 * @param {{companyId: string|null, count: number}} latest - l'ultimo conteggio letto, con la sua azienda
 * @param {string|null|undefined} activeCompanyId
 */
export function shownUnread(latest, activeCompanyId) {
  return latest && latest.companyId && latest.companyId === activeCompanyId ? latest.count : 0;
}

/** La pagina mostra ancora la fattura e l'azienda su cui un'azione è partita. */
export function stillShowing(started, now) {
  return !now.destroyed && now.invoiceId === started.invoiceId && now.companyId === started.companyId;
}

/**
 * Rimette una fattura tra quelle da leggere e, solo se la pagina mostra ancora
 * quella fattura di quell'azienda, torna all'elenco delle ricevute: restare sul
 * dettaglio la segnerebbe di nuovo come letta. Chi nel frattempo è andato
 * altrove non viene riportato indietro.
 *
 * @param {{invoiceId: string, companyId: string}} started
 * @param {{setReadState: Function, refresh: Function, navigate: Function,
 *   current: () => {invoiceId: string, companyId: string, destroyed: boolean}}} deps
 * A failure is handed back only if the page still shows what the action
 * started on: a detail page the router reused for another invoice must not show
 * the first one's error.
 *
 * @returns {Promise<{navigated: boolean, error: Error|null}>}
 */
export async function markUnreadAndReturn(started, { setReadState, refresh, navigate, current }) {
  try {
    await setReadState(started.companyId, { ids: [started.invoiceId], isRead: false });
    await refresh(started.companyId);
  } catch (error) {
    return { navigated: false, error: stillShowing(started, current()) ? error : null };
  }
  if (!stillShowing(started, current())) return { navigated: false, error: null };
  navigate('/invoices?direction=inbound');
  return { navigated: true, error: null };
}

/**
 * «Segna come lette» per l'azienda su cui l'azione è partita.
 *
 * Elenco e contatore si ricaricano solo se quell'azienda è ancora attiva, e un
 * errore viene restituito solo in quel caso: un rifiuto arrivato dopo il
 * passaggio a un'altra azienda non deve comparire sotto quella.
 *
 * @param {{companyId: string, target: {ids?: string[], all?: true}}} started
 * @param {{setReadState: Function, reload: (companyId: string) => Promise<unknown>,
 *   currentCompanyId: () => string|null|undefined}} deps
 * @returns {Promise<{error: Error|null}>}
 */
export async function markReadForCompany({ companyId, target }, { setReadState, reload, currentCompanyId }) {
  try {
    await setReadState(companyId, { ...target, isRead: true });
    if (currentCompanyId() === companyId) await reload(companyId);
  } catch (error) {
    return { error: currentCompanyId() === companyId ? error : null };
  }
  return { error: null };
}
